import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_INPUT,
  AE_INPUT_COMPACT,
  AE_LABEL,
  AE_LABEL_COMPACT,
  AE_SELECT,
  AE_SELECT_COMPACT,
} from '@/components/assessment-engine/layout/aeTheme';
import type {
  AeCompletionOptions,
  AeFollowUpConfig,
  AePresentationStyle,
  AeRespondentConfig,
} from '@/types/assessmentEngine';
import { AE_DEFAULT_RESPONDENT_CONFIG } from '@/types/assessmentEngine';

export interface CampaignRespondentSetupValue {
  presentation_style: AePresentationStyle;
  respondent_config: AeRespondentConfig;
}

interface CampaignRespondentSetupFieldsProps {
  value: CampaignRespondentSetupValue;
  onChange: (next: CampaignRespondentSetupValue) => void;
  /** Require email OTP before take session (respondent experience). */
  emailVerificationRequired: boolean;
  onEmailVerificationChange: (enabled: boolean) => void;
  /** Editable on open/closed campaigns (same rule as messaging — not archived). */
  emailVerificationEditable?: boolean;
  showInstantResults: boolean;
  onInstantResultsChange: (enabled: boolean) => void;
  benchmarkMinResponses: number;
  onBenchmarkMinResponsesChange: (value: number) => void;
  /** Share link is only meaningful for public campaigns. */
  distributionMode?: 'public' | 'invite_only';
  /** Open campaigns for invite-others target (admin picks, REQ-AE-054). */
  inviteTargetCampaigns?: Array<{ id: string; name: string }>;
  currentCampaignId?: string;
  /** New-campaign wizard: default invite-others target is this campaign. */
  defaultInviteTargetSelf?: boolean;
  disabled?: boolean;
  /** When true, benchmark minimum field stays editable even if other fields are disabled. */
  benchmarkMinEditable?: boolean;
  /** Denser hub layout: compact controls and tighter fieldsets. */
  compact?: boolean;
}

function followUp(cfg: AeRespondentConfig): AeFollowUpConfig {
  return cfg.follow_up ?? AE_DEFAULT_RESPONDENT_CONFIG.follow_up!;
}

function completion(cfg: AeRespondentConfig): AeCompletionOptions {
  return cfg.completion_options ?? AE_DEFAULT_RESPONDENT_CONFIG.completion_options!;
}

/** Shared campaign respondent experience fields (wizard + hub config). */
export const CampaignRespondentSetupFields: React.FC<CampaignRespondentSetupFieldsProps> = ({
  value,
  onChange,
  emailVerificationRequired,
  onEmailVerificationChange,
  emailVerificationEditable = true,
  showInstantResults,
  onInstantResultsChange,
  benchmarkMinResponses,
  onBenchmarkMinResponsesChange,
  distributionMode = 'public',
  inviteTargetCampaigns = [],
  currentCampaignId,
  defaultInviteTargetSelf = false,
  disabled = false,
  benchmarkMinEditable = true,
  compact = false,
}) => {
  const { t } = useLanguage();
  const fu = followUp(value.respondent_config);
  const co = completion(value.respondent_config);

  const benchmarkFieldDisabled = disabled && !benchmarkMinEditable;
  const emailVerificationDisabled = disabled && !emailVerificationEditable;
  const labelClass = compact ? AE_LABEL_COMPACT : AE_LABEL;
  const inputClass = compact ? AE_INPUT_COMPACT : AE_INPUT;
  const selectClass = compact ? AE_SELECT_COMPACT : AE_SELECT;
  const stackGap = compact ? 'space-y-2' : 'space-y-4';
  const fieldsetPad = compact ? 'space-y-1.5 p-2.5' : 'space-y-2 p-3';

  const patchRespondent = (patch: Partial<AeRespondentConfig>) => {
    onChange({
      ...value,
      respondent_config: { ...value.respondent_config, ...patch },
    });
  };

  return (
    <div className={stackGap}>
      <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
        <input
          type="checkbox"
          checked={emailVerificationRequired}
          disabled={emailVerificationDisabled}
          onChange={(e) => onEmailVerificationChange(e.target.checked)}
        />
        {t('assessmentEngine.wizard.emailVerification')}
      </label>

      <div className={compact ? 'max-w-xs' : undefined}>
        <label className={labelClass}>{t('assessmentEngine.wizard.presentationStyle')}</label>
        <select
          className={selectClass}
          value={value.presentation_style}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...value, presentation_style: e.target.value as AePresentationStyle })
          }
        >
          <option value="question">{t('assessmentEngine.presentationStyle.question')}</option>
          <option value="section">{t('assessmentEngine.presentationStyle.section')}</option>
          <option value="subsection">{t('assessmentEngine.presentationStyle.subsection')}</option>
        </select>
      </div>

      <fieldset className={`${fieldsetPad} border border-[var(--color-border)] rounded`}>
        <legend className="text-sm font-medium text-[var(--color-surface-text)] px-1">
          {t('assessmentEngine.wizard.completionOptions')}
        </legend>
        <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
          <input
            type="checkbox"
            checked={showInstantResults}
            disabled={disabled}
            onChange={(e) => onInstantResultsChange(e.target.checked)}
          />
          {t('assessmentEngine.wizard.instantResults')}
        </label>
        <div className={compact ? 'max-w-[10rem]' : undefined}>
          <label className={labelClass}>{t('assessmentEngine.wizard.benchmarkMinResponses')}</label>
          <input
            type="number"
            min={1}
            className={inputClass}
            disabled={benchmarkFieldDisabled}
            value={benchmarkMinResponses}
            onChange={(e) =>
              onBenchmarkMinResponsesChange(Math.max(1, Number(e.target.value) || 1))
            }
          />
          <p className="mt-0.5 text-xs text-[var(--color-secondary-text)]">
            {t('assessmentEngine.wizard.benchmarkMinResponsesHelp')}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
          <input
            type="checkbox"
            checked={!!co.allow_invite_others}
            disabled={disabled}
            onChange={(e) =>
              patchRespondent({
                completion_options: { ...co, allow_invite_others: e.target.checked },
              })
            }
          />
          {t('assessmentEngine.wizard.allowInviteOthers')}
        </label>
        {co.allow_invite_others &&
          (defaultInviteTargetSelf || currentCampaignId || inviteTargetCampaigns.length > 0) && (
            <div>
              <label className={labelClass}>{t('assessmentEngine.wizard.inviteTargetCampaign')}</label>
              <select
                className={selectClass}
                disabled={disabled}
                value={
                  value.respondent_config.invite_target_campaign_id ??
                  currentCampaignId ??
                  (defaultInviteTargetSelf ? '' : '')
                }
                onChange={(e) =>
                  patchRespondent({
                    invite_target_campaign_id: e.target.value || null,
                  })
                }
              >
                {(defaultInviteTargetSelf || currentCampaignId) && (
                  <option value={currentCampaignId ?? ''}>
                    {t('assessmentEngine.wizard.thisCampaign')}
                  </option>
                )}
                {inviteTargetCampaigns
                  .filter((c) => c.id !== currentCampaignId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          )}
        <div className={compact ? 'flex flex-wrap gap-x-4 gap-y-1' : 'space-y-2'}>
          <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
            <input
              type="checkbox"
              checked={!!co.allow_cohort_updates}
              disabled={disabled}
              onChange={(e) =>
                patchRespondent({
                  completion_options: { ...co, allow_cohort_updates: e.target.checked },
                })
              }
            />
            {t('assessmentEngine.wizard.allowCohortUpdates')}
          </label>
          {distributionMode === 'public' && (
            <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
              <input
                type="checkbox"
                checked={!!co.allow_share_link}
                disabled={disabled}
                onChange={(e) =>
                  patchRespondent({
                    completion_options: { ...co, allow_share_link: e.target.checked },
                  })
                }
              />
              {t('assessmentEngine.wizard.allowShareLink')}
            </label>
          )}
        </div>
      </fieldset>

      <fieldset className={`${fieldsetPad} border border-[var(--color-border)] rounded`}>
        <legend className="text-sm font-medium text-[var(--color-surface-text)] px-1">
          {t('assessmentEngine.wizard.followUp')}
        </legend>
        <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
          <input
            type="checkbox"
            checked={!!fu.enabled}
            disabled={disabled}
            onChange={(e) =>
              patchRespondent({
                follow_up: { ...fu, enabled: e.target.checked },
              })
            }
          />
          {t('assessmentEngine.wizard.followUpEnabled')}
        </label>
        {fu.enabled && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>{t('assessmentEngine.wizard.followUpFrequency')}</label>
              <input
                type="number"
                min={1}
                className={inputClass}
                disabled={disabled}
                value={fu.frequency_days}
                onChange={(e) =>
                  patchRespondent({
                    follow_up: { ...fu, frequency_days: Math.max(1, Number(e.target.value) || 7) },
                  })
                }
              />
            </div>
            <div>
              <label className={labelClass}>{t('assessmentEngine.wizard.followUpMax')}</label>
              <input
                type="number"
                min={1}
                max={10}
                className={inputClass}
                disabled={disabled}
                value={fu.max_reminders}
                onChange={(e) =>
                  patchRespondent({
                    follow_up: { ...fu, max_reminders: Math.max(1, Number(e.target.value) || 3) },
                  })
                }
              />
            </div>
          </div>
        )}
      </fieldset>
    </div>
  );
};
